
import {defineComponent, PropType} from "vue";
import {CodeDescriptionRestDto} from "@/api/pharma-cms-content-block/models";
import {ColorSolidRestDtoModel} from "@/models/api/ColorSolidRestDtoModel";
import {ColorRestDtoModel} from "@/models/api/ColorRestDtoModel";
import {ColorModeEnum} from "@/models/enum/ColorModeEnum";
import ColorPicker from "@/components/UI/ColorPicker.vue";
import InputSelect from "@/components/UI/InputSelect.vue";
import {ComponentMixin} from "@/components/base/ComponentMixin";
import GenericComponentWrapper from "@/components/base/GenericComponentWrapper.vue";
import {EnumRestService} from "@/services/rest/enum/EnumRestService";
import {EnumClassNameCmsEnum} from "@/models/enum/EnumClassNameCmsEnum";
import {DpModuleEnum} from "@/api/enums/dp-module-enum";
import {ColorSolidRestDto, TextColorSolidRestDto} from "@/api/pharma-cms-content-page/models";
import {TextColorSolidRestDtoModel} from "@/models/api/TextColorSolidRestDtoModel";

export default defineComponent({
  name: "ColorSolidEditor",
  mixins: [ComponentMixin],
  components: {GenericComponentWrapper, ColorPicker, InputSelect},
  emits: ['update:modelValue'],
  props: {
    // Null is only a valid value together with allowEmpty; without it every caller passes a
    // colour and the editor never emits anything else.
    modelValue: {
      type: Object as PropType<ColorSolidRestDto | TextColorSolidRestDto | null>,
      required: false,
      default: null,
    },
    label: {
      type: String,
      required: false,
      default: '',
    },
    name: {
      type: String,
      required: true,
    },
    isBackground: {
      type: Boolean,
      required: false,
      default: false
    },
    // Greys out every control without hiding them, so the value stays readable as a reference.
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    // Makes "no colour at all" a valid state: the colour becomes optional instead of required,
    // the dropdown gets a clear button, and an empty colour is emitted as null. For hosts where
    // the colour may simply be left out, such as a box shadow that falls back to the stylesheet.
    allowEmpty: {
      type: Boolean,
      required: false,
      default: false,
    },
    // Lets the colour dropdown fill the whole width instead of a third of it. For narrow hosts
    // such as the per-side border panels, where a third of the width is unreadably small.
    fullWidth: {
      type: Boolean,
      required: false,
      default: false,
    },
    errors: {
      type: Object,
      required: true,
    },
  },
  mounted() {
    this.initialize();
  },
  data() {
    return {
      localDto: (this.isBackground
        ? ColorSolidRestDtoModel.createWithDefaults()
        : TextColorSolidRestDtoModel.createWithDefaults()) as ColorSolidRestDto | TextColorSolidRestDto,
      enumTextColorCode: [] as CodeDescriptionRestDto[],
      enumBackgroundColorCode: [] as CodeDescriptionRestDto[],
    };
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(newValue: ColorSolidRestDto | TextColorSolidRestDto | null) {
        if (this.isBackground) {
          this.localDto = newValue
            ? ColorSolidRestDtoModel.cloneFrom(newValue)
            : ColorSolidRestDtoModel.createWithDefaults();
        } else {
          this.localDto = newValue
            ? TextColorSolidRestDtoModel.cloneFrom(newValue)
            : TextColorSolidRestDtoModel.createWithDefaults();
        }
      },
    },
  },
  computed: {
    /**
     * A disabled field cannot be filled in by the user, so requiring it would block the form with
     * an error nobody can act on. That happens on the border's shared section, which stays visible
     * but disabled while the individual sides are being edited.
     */
    colorRules(): string | undefined {
      return this.disabled || this.allowEmpty ? undefined : 'required';
    },
    /**
     * Neither a token nor a custom colour: the host is left without a colour.
     */
    isEmpty(): boolean {
      if (this.localDto.rgba) {
        return false;
      }

      return this.isBackground
        ? !(this.localDto as ColorSolidRestDto).color_code
        : !(this.localDto as TextColorSolidRestDto).text_color_code;
    },
    ColorModeEnum() {
      return ColorModeEnum;
    },
    colorMode(): ColorModeEnum {
      if (this.localDto.rgba) {
        return ColorModeEnum.RGBA;
      }
      return ColorModeEnum.TOKEN;
    }
  },
  methods: {
    async doInitialize(): Promise<void> {
      [
        this.enumTextColorCode,
        this.enumBackgroundColorCode,
      ] = await Promise.all([
        EnumRestService.getInstance().getEnumValuesByClassName(EnumClassNameCmsEnum.TEXT_COLOR, DpModuleEnum.CMS),
        EnumRestService.getInstance().getEnumValuesByClassName(EnumClassNameCmsEnum.BACKGROUND_COLOR, DpModuleEnum.CMS),
      ]);
    },
    setColorMode(mode: ColorModeEnum): void {
      if (mode === ColorModeEnum.TOKEN) {
        this.localDto.rgba = null;
      } else { // RGBA
        if (this.isBackground) {
          (this.localDto as ColorSolidRestDto).color_code = null;
        } else {
          (this.localDto as TextColorSolidRestDto).text_color_code = null;
        }
        if (!this.localDto.rgba) {
          this.localDto.rgba = ColorRestDtoModel.createWithDefaults();
        }
      }
      this.emitModelChanged();
    },
    emitModelChanged(): void {
      // Clearing the dropdown leaves an object with nothing in it, which the API would reject as
      // an incomplete colour. Where a colour may be left out, that state is "no colour" instead.
      if (this.allowEmpty && this.isEmpty) {
        this.$emit('update:modelValue', null);
        return;
      }

      this.$emit('update:modelValue', this.localDto);
    }
  },
});
